import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { createSessionEventSource } from '../services/realtimeService';

const SessionRealtimeContext = createContext(null);

export function SessionRealtimeProvider({ children }) {
  const [sessionStatus, setSessionStatus] = useState(null);
  const [eventVersion, setEventVersion] = useState(0);

  const refreshSessionStatus = async () => {
    try {
      setSessionStatus(await api.getSessionStatus());
    } catch {
      // keep last known state
    }
  };

  useEffect(() => {
    refreshSessionStatus();
    const source = createSessionEventSource();
    const bump = () => setEventVersion((value) => value + 1);

    if (!source) {
      const interval = setInterval(() => {
        refreshSessionStatus();
      }, 2000);

      return () => clearInterval(interval);
    }

    source.addEventListener('session:update', (event) => {
      setSessionStatus(JSON.parse(event.data));
      bump();
    });
    ['players:update', 'player:joined', 'player:progress', 'round:completed', 'round:unlocked', 'game:finished', 'game:stopped', 'game:paused', 'game:resumed'].forEach((eventName) => {
      source.addEventListener(eventName, bump);
    });
    source.onerror = () => {
      refreshSessionStatus();
    };

    return () => source.close();
  }, []);

  const value = useMemo(() => ({ sessionStatus, refreshSessionStatus, eventVersion }), [sessionStatus, eventVersion]);
  return <SessionRealtimeContext.Provider value={value}>{children}</SessionRealtimeContext.Provider>;
}

export function useSessionRealtime() {
  const context = useContext(SessionRealtimeContext);
  if (!context) throw new Error('useSessionRealtime must be used inside SessionRealtimeProvider');
  return context;
}
